import React, { useState, useEffect } from 'react';
import { 
  Box, 
  Checkbox, 
  TextField, 
  Button, 
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  FormGroup,
  FormControlLabel,
  IconButton
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import './PeakHourConfig.scss';
import rateService from '../../services/rateService';

const DAYS = [
  { value: 'Mon', label: 'Mon' },
  { value: 'Tue', label: 'Tue' },
  { value: 'Wed', label: 'Wed' },
  { value: 'Thu', label: 'Thu' },
  { value: 'Fri', label: 'Fri' },
  { value: 'Sat', label: 'Sat' },
  { value: 'Sun', label: 'Sun' }
];

const RIDE_TYPE_MAP = {
  // Define your ride type mappings here
};

const PeakHourConfig = ({ locationId, carType, rideType, companyId }) => {
  const [peakHours, setPeakHours] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [editIndex, setEditIndex] = useState(-1);
  const [selectedSlotId, setSelectedSlotId] = useState(null);
  const [selectedDays, setSelectedDays] = useState([]);
  const [startTime, setStartTime] = useState('');
  const [endTime, setEndTime] = useState('');
  const [extraCharge, setExtraCharge] = useState('');

    const fetchPeakHours = async () => {
      if (!locationId || !carType || !rideType) return;

      setIsLoading(true);
      try {
        const result = await rateService.getPeakHourConfig({
          location: locationId,
          tripType: rideType,
          carType
        });

        if (result.success && result.data?.data?.slots) {
          // Format the slots data for the table
          const formattedPeakHours = result.data?.data?.slots.map(slot => ({
            id: slot.id,
            days: [slot.day], // Convert full day name to abbreviated if needed
            startTime: slot.startTime,
            endTime: slot.endTime,
            extraCharge: slot.adjustmentPercent,
            metadata: slot.RateCardConfig // Store additional metadata if needed
          }));
          setPeakHours(formattedPeakHours);
        } else {
          setPeakHours([]); // Set empty array if no data
        }
      } catch (error) {
        console.error('Error fetching peak hours:', error);
        setPeakHours([]); // Set empty array on error
      } finally {
        setIsLoading(false);
      }
    };

useEffect(() => {
  fetchPeakHours();

  if(rideType == 'Outstation') {
    setStartTime('00:00');
    setEndTime('23:59');
  }
},[locationId, carType, rideType, companyId])  

const handleDayChange = (day) => {
    setSelectedDays(prev => 
      prev.includes(day) 
        ? prev.filter(d => d !== day)
        : [...prev, day]
    );
  };

  const handleAddPeakHour = async () => {
    try {
      if (selectedSlotId) {
        // Update existing slot
        const result = await rateService.updatePeakHourSlot(selectedSlotId, {
          day: selectedDays[0],
          startTime,
          endTime,
          extraCharge
        });

        if (result.success) {
          await fetchPeakHours(); // Use the existing fetchPeakHours function
        }
      } else {
        // Create new peak hour config
        const result = await rateService.createPeakHourConfig({
          locationId,
          carType,
          rideType,
          slots: selectedDays.map(day => ({
            day,
            startTime,
            endTime,
            extraCharge
          }))
        });

        if (result.success) {
          await fetchPeakHours(); // Use the existing fetchPeakHours function
        }
      }

      // Reset form
      resetForm();
    } catch (error) {
      console.error('Error saving peak hour:', error);
    }
  };

  const handleEdit = (index) => {
    const peakHour = peakHours[index];
    setSelectedDays([getAbbreviatedDay(peakHour.days[0])]);
    setStartTime(peakHour.startTime);
    setEndTime(peakHour.endTime);
    setExtraCharge(peakHour.extraCharge);
    setEditIndex(index);
    setSelectedSlotId(peakHour.id);
  };

  const handleDelete = async (index) => {
    try {
      const slotId = peakHours[index].id;

        const result = await rateService.deletePeakHourSlot(slotId);
        
        if (result.success) {
          await fetchPeakHours();
          resetForm();
        } else {
          // Handle error - maybe show an error message
          console.error('Failed to delete peak hour:', result.error);
        }
    } catch (error) {
      console.error('Error deleting peak hour:', error);
    }
  };

  const resetForm = () => {
    setSelectedDays([]);
    setStartTime(rideType == 'Outstation' ? '00:00' : '');
    setEndTime(rideType == 'Outstation' ? '23:59' : '');
    setExtraCharge('');
    setEditIndex(-1);
    setSelectedSlotId(null);
  };

  // Add a helper function to convert full day name to abbreviated version
  const getAbbreviatedDay = (fullDay) => {
    const dayMap = {
      'Monday': 'Mon',
      'Tuesday': 'Tue',
      'Wednesday': 'Wed',
      'Thursday': 'Thu',
      'Friday': 'Fri',
      'Saturday': 'Sat',
      'Sunday': 'Sun'
    };
    return dayMap[fullDay] || fullDay;
  };

  const handleNumberInput = (e) => {
    e.target.addEventListener('wheel', (e) => e.preventDefault(), { passive: false });
  };

  return (
    <Box className="peak-hour-config">
      <h3 className='text-[#151516] font-semibold'>{rideType == 'Outstation' ? "Peak Days Configuration" : "Peak Hour Configuration"}</h3>
      {isLoading ? (
        <div>Loading peak hours...</div>
      ) : (
        <>
          <Box className="peak-hour-form">
            <FormGroup row className="days-selection">
              {DAYS.map(day => (
                <FormControlLabel
                  key={day.value}
                  control={
                    <Checkbox
                      checked={selectedDays.includes(day.value)}
                      onChange={() => handleDayChange(day.value)}
                    />
                  }
                  label={day.label}
                />
              ))}
            </FormGroup>

            <Box className="time-config">
              <TextField
                type="time"
                label="Start Time"
                value={startTime}
                onChange={(e) => setStartTime(e.target.value)}
                InputLabelProps={{ shrink: true }}
                disabled={rideType == 'Outstation'}
              />
              <TextField
                type="time"
                label="End Time"
                value={endTime}
                onChange={(e) => setEndTime(e.target.value)}
                InputLabelProps={{ shrink: true }}
                disabled={rideType == 'Outstation'}
              />
              <TextField
                type="number"
                label="Extra Charge (%)"
                value={extraCharge}
                onChange={(e) => {
                  const value = e.target.value;
                  // Allow empty string or numbers between 0-100
                  if (value === '' || (Number(value) >= 0 && Number(value) <= 100)) {
                    setExtraCharge(value);
                  }
                }}
                onKeyDown={(e) => {
                  // Only prevent minus/plus signs
                  if (e.key === '-' || e.key === '+') {
                    e.preventDefault();
                  }
                }}
                onFocus={handleNumberInput}
                inputProps={{
                  min: 0,
                  max: 100,
                  step: 'any'
                }}
                // Validate on blur to ensure final value is within range
                onBlur={(e) => {
                  const value = e.target.value;
                  if (value === '') {
                    setExtraCharge('');
                  } else {
                    const numValue = Number(value);
                    if (numValue > 100) setExtraCharge('100');
                    if (numValue < 0) setExtraCharge('0');
                  }
                }}
                InputProps={{
                  endAdornment: <span>%</span>
                }}
              />
            </Box>

            <Button 
              variant="contained" 
              color="primary"
              onClick={handleAddPeakHour}
              disabled={!selectedDays.length || !startTime || !endTime || !extraCharge}
              sx={{textTransform: "none"}}
            >
              {editIndex >= 0 ? 'Update Peak Hour' : 'Add Peak Hour'}
            </Button>
          </Box>

          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Day</TableCell>
                <TableCell>Start Time</TableCell>
                <TableCell>End Time</TableCell>
                <TableCell>Peak Hour Extra (%)</TableCell>
                <TableCell>Action</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {peakHours && peakHours?.map((peakHour, index) => (
                <TableRow key={peakHour.id}>
                  <TableCell>{peakHour.days.map(day => getAbbreviatedDay(day)).join(', ')}</TableCell>
                  <TableCell>{peakHour.startTime}</TableCell>
                  <TableCell>{peakHour.endTime}</TableCell>
                  <TableCell>{parseFloat(peakHour.extraCharge).toFixed(2)}%</TableCell>
                  <TableCell>
                    <IconButton onClick={() => handleEdit(index)}>
                      <EditIcon />
                    </IconButton>
                    <IconButton onClick={() => handleDelete(index)}>
                      <DeleteIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </>
      )}
    </Box>
  );
};

export default PeakHourConfig;